import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { ChallengeBoard, ChallengeBoardKind } from '@radevents/shared';
import ChallengeDetailHeader from '../components/winterchallenge/ChallengeDetailHeader';
import { Leaderboard } from '../components/winterchallenge/ChallengeParts';
import { messageOf } from '../components/winterchallenge/messageOf';
import { useChallengeSummary } from '../components/WinterchallengePromoBanner';
import { Spinner } from '../components/ui';
import challengeService from '../services/challengeService';

const KINDS: ChallengeBoardKind[] = ['riders', 'teams', 'regions', 'countries'];
const STEP = 50;
const DESC = 'Die ganze Wertung der Winterchallenge: Einzel, Teams, Bundesländer und Länder.';

/** `/winterchallenge/rangliste/:kind` — die ganze Wertung, in Stücken zu 50 nachgeladen. */
const WinterchallengeRangliste: React.FC = () => {
  const { kind: raw = 'riders' } = useParams();
  const kind: ChallengeBoardKind = KINDS.includes(raw as ChallengeBoardKind) ? raw as ChallengeBoardKind : 'riders';
  const navigate = useNavigate();
  const summary = useChallengeSummary();
  const [board, setBoard] = useState<ChallengeBoard | null>(null);
  /** Abruf gescheitert: „nicht erreichbar“ statt einer leeren Wertung. */
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setBoard(null);
    setFailed(false);
    challengeService.board(kind, 0, STEP).then(setBoard).catch(() => setFailed(true));
  }, [kind]);

  const more = () => {
    if (!board) return;
    setLoading(true);
    challengeService.board(kind, board.rows.length, STEP)
      .then(next => setBoard({ ...next, rows: [...board.rows, ...next.rows], me: board.me && !next.rows.some(r => r.isMe) ? board.me : null }))
      .catch(err => toast.error(messageOf(err, 'Weitere Plätze konnten nicht geladen werden.')))
      .finally(() => setLoading(false));
  };

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>Rangliste | Winterchallenge | Radsport Events</title>
        <meta name="description" content={DESC} />
        {/* Die volle Liste ändert sich stündlich; indexiert wird die Challenge-Seite selbst. */}
        <meta name="robots" content="noindex" />
      </Helmet>
      <ChallengeDetailHeader />
      <main style={{ flex: 1 }}>
        <div className="wc-page" style={{ maxWidth: 760 }}>
          <Link to="/winterchallenge" className="wc-link wc-link--back hide-mobile">
            <ArrowLeft size={15} aria-hidden />Winterchallenge
          </Link>
          {board || failed ? (
            <>
              <Leaderboard key={kind} boards={board ? { [kind]: board } : {}} unavailable={{ [kind]: failed }} summary={summary} initialKind={kind}
                onKindChange={k => navigate(`/winterchallenge/rangliste/${k}`, { replace: true })} />
              {board && board.rows.length < board.total && (
                <button type="button" className="wc-btn wc-btn--ghost" onClick={more} disabled={loading} style={{ justifySelf: 'center' }}>
                  {loading ? 'Lädt …' : 'Weitere anzeigen'}
                </button>
              )}
            </>
          ) : <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>}
        </div>
      </main>
    </div>
  );
};

export default WinterchallengeRangliste;
